import { has } from 'lodash';

import { ampFetch } from '~/account/api/api';

export const validatePromoCode = async (promoCode) => {
    const response = await ampFetch({
        route: 'api-user/offers/promo-codes/validate',
        options: {
            method: 'POST',
            body: JSON.stringify({ code: promoCode })
        }
    });

    if (response.success) {
        return { success: true, promoCode: response.data };
    }

    return { success: false, error: response.data };
};

/**
 * Decide which cart items a promo code should attach to at preview time,
 * given the code's redemption cap and applicability rules.
 *
 * Pure function — no API calls, no side effects.
 *
 * @param {Array} items                Current cart items.
 * @param {Object} code                Discount/promo object from cart state.
 * @param {string} [code.applicable_to]
 * @param {?number} [code.max_total_redemptions]
 * @param {?number} [code.max_redemptions_per_user]
 * @param {number} [code.redemption_count]
 * @returns {{ selectedCartItemIds: Array, notAppliedReason: ?string }}
 *   notAppliedReason ∈ { 'limit_reached', 'no_eligible_items', null }
 */
export const pickItemsForCode = (items = [], code = {}) => {
    const {
        applicable_to: applicableTo = 'all',
        max_total_redemptions: maxTotal = null,
        max_redemptions_per_user: maxPerUser = null,
        redemption_count: redemptionCount = 0
    } = code;

    const perUserCap = maxPerUser == null ? Infinity : maxPerUser;
    const remainingTotal =
        maxTotal == null
            ? Infinity
            : Math.max(0, maxTotal - (redemptionCount || 0));
    const cap = Math.max(0, Math.min(perUserCap, remainingTotal));

    const appliesToSubscriptions =
        applicableTo === 'all' || applicableTo === 'subscription';
    const appliesToSingleWash =
        applicableTo === 'all' || applicableTo === 'single_wash';
    const appliesToAddon = applicableTo === 'addon';

    const isEligible = (item) => {
        if (!item) return false;

        // Physical items rationed server-side via Stripe; always exclude here.
        if (
            item.productType === 'physical' ||
            (item.productType === 'gift-card' && item.isPhysicalGiftCard)
        ) {
            return false;
        }

        // Skip BOGO prepaid promotion items (matches validateDiscounts).
        if (item.bogoPrepaidPromotion) return false;

        if (item.productType === 'subscription') {
            return appliesToSubscriptions;
        }

        if (item.productType === 'digital') {
            // Digital items can't preview without a vehicle (matches
            // validateDiscounts skip).
            if (!has(item, 'vehicle')) return false;

            if (appliesToAddon) {
                const nonBellsAddOns = (item.addOns || []).filter(
                    (a) => !a.useBells
                );
                return nonBellsAddOns.length > 0;
            }

            return appliesToSingleWash;
        }

        return false;
    };

    const eligible = items.filter(isEligible);

    if (eligible.length === 0) {
        return {
            selectedCartItemIds: [],
            notAppliedReason: cap === 0 ? 'limit_reached' : 'no_eligible_items'
        };
    }

    if (cap === 0) {
        return {
            selectedCartItemIds: [],
            notAppliedReason: 'limit_reached'
        };
    }

    // Rank by price desc; tie-break by cartItemId for determinism.
    const ranked = [...eligible].sort((a, b) => {
        const priceDiff = (b.price || 0) - (a.price || 0);
        if (priceDiff !== 0) return priceDiff;
        const aId = String(a.cartItemId ?? '');
        const bId = String(b.cartItemId ?? '');
        return aId < bId ? -1 : aId > bId ? 1 : 0;
    });

    const selected = ranked.slice(0, cap);

    return {
        selectedCartItemIds: selected.map((i) => i.cartItemId),
        notAppliedReason: null
    };
};
